// =============================================================================
// features/meu-whatsapp/PairingQrPanel.tsx — Botão "Conectar" + QR do vendedor
// (extraído de MeuWhatsAppPage no F56-S03).
//
// Usado em dois lugares com o MESMO comportamento: a página /meu-whatsapp e o
// convite que o AppLayout abre (ConnectWhatsAppPrompt). Nenhum dos dois repete
// o bloco do QR.
//
// Só renderiza para quem NÃO está conectado — quem chama decide o que mostrar
// quando o aparelho já está WORKING.
//
// LGPD: o QR é credencial de pareamento. Vive só neste componente (query com
// `gcTime: 0`), nunca vai para storage, log ou URL, e some quando o painel
// desmonta (fechar o convite, sair da página). `autoStart` só dispara a sessão
// quando o painel monta — o convite monta o painel DEPOIS do aceite da política.
// =============================================================================

import * as React from 'react';

import { cn } from '../../lib/cn';

import { shouldOfferManualRetry } from './connectPrompt';
import { MEU_STATUS_LABEL, useIniciarMeuPareamento, useMeuQr } from './useMeuWhatsApp';
import type { MeuStatus } from './useMeuWhatsApp';

export interface PairingQrPanelProps {
  readonly status: MeuStatus;
  /** O vendedor já tem canal criado (`channel_id` não nulo). */
  readonly hasChannel: boolean;
  /**
   * Inicia a sessão sozinho ao montar (uma vez), sem exigir o clique em
   * "Conectar meu WhatsApp". Usado pelo convite, onde o clique já aconteceu.
   */
  readonly autoStart?: boolean;
  /** Sem borda, sombra e padding próprios — para viver dentro de outro cartão. */
  readonly bare?: boolean;
}

function Spinner({ label }: { readonly label: string }): React.JSX.Element {
  return (
    <div
      className="w-6 h-6 rounded-full border-2 border-border-strong border-t-azul animate-spin"
      role="status"
      aria-label={label}
    />
  );
}

export function PairingQrPanel({
  status,
  hasChannel,
  autoStart = false,
  bare = false,
}: PairingQrPanelProps): React.JSX.Element {
  const iniciar = useIniciarMeuPareamento();

  const pareando = hasChannel && status !== 'WORKING';
  const precisaIniciar = status === 'STOPPED' || status === 'FAILED';

  // Com `autoStart` o canal pode existir parado (STOPPED/FAILED): pedir o QR
  // antes de a sessão subir só devolveria erro. Sem `autoStart` o comportamento
  // é o histórico da página: pede o QR assim que há canal.
  const qrEnabled = autoStart ? pareando && !precisaIniciar : pareando;
  const qr = useMeuQr(qrEnabled);

  const startedRef = React.useRef(false);
  const [jaIniciou, setJaIniciou] = React.useState(false);
  const { mutate: iniciarSessao, isPending: iniciando } = iniciar;
  React.useEffect(() => {
    if (!autoStart || startedRef.current || !precisaIniciar || iniciando) return;
    startedRef.current = true;
    // `jaIniciou` só vira true quando a 1ª tentativa TERMINA: durante ela o
    // painel fica no spinner, sem piscar o botão manual.
    iniciarSessao(undefined, { onSettled: () => setJaIniciou(true) });
  }, [autoStart, precisaIniciar, iniciando, iniciarSessao]);

  // Só a PRIMEIRA tentativa é automática. Depois dela (falha ao iniciar ou QR
  // expirado com a sessão parada) a próxima é um clique explícito — nunca um
  // reinício automático. Spinner só com algo em andamento.
  const ofereceBotaoManual = shouldOfferManualRetry({
    autoStart,
    precisaIniciar,
    iniciando,
    jaIniciou,
    falhouAoIniciar: iniciar.isError,
  });
  const mostrarBotaoConectar = autoStart ? ofereceBotaoManual : !pareando;
  const iniciandoAutomatico = autoStart && precisaIniciar && !ofereceBotaoManual;

  if (mostrarBotaoConectar) {
    return (
      <button
        type="button"
        onClick={() => {
          iniciar.mutate();
        }}
        disabled={iniciar.isPending}
        className={cn(
          'font-sans text-sm font-semibold px-4 py-3 rounded-sm min-h-[44px]',
          'bg-azul text-white',
          'transition-[background,transform] duration-fast hover:brightness-110',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-azul/30',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      >
        {iniciar.isPending
          ? 'Gerando código…'
          : jaIniciou
            ? 'Gerar outro código'
            : 'Conectar meu WhatsApp'}
      </button>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-4',
        bare ? '' : 'rounded-md border border-border bg-surface-1 p-5',
      )}
      style={bare ? undefined : { boxShadow: 'var(--elev-2)' }}
    >
      <p className="font-sans text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">
        {MEU_STATUS_LABEL[status]}
      </p>

      <ol className="font-sans text-sm text-ink-2 flex flex-col gap-1.5 list-decimal list-inside">
        <li>Abra o WhatsApp no seu celular</li>
        <li>
          Toque em <strong className="font-semibold text-ink">Aparelhos conectados</strong>
        </li>
        <li>Aponte a câmera para o código abaixo</li>
      </ol>

      <div className="flex items-center justify-center min-h-[240px] rounded-sm border border-border-strong bg-surface-2 p-3">
        {qr.isLoading || iniciandoAutomatico || (autoStart && !qrEnabled) ? (
          <Spinner label="Gerando código…" />
        ) : qr.isError ? (
          <p className="font-sans text-sm text-danger text-center px-4">
            Não foi possível gerar o código agora. Toque em “Gerar outro código”.
          </p>
        ) : qr.data !== undefined ? (
          <img
            src={`data:${qr.data.mime_type};base64,${qr.data.qr_base64}`}
            alt="Código para conectar seu WhatsApp"
            className="w-[220px] h-[220px] rounded-sm"
          />
        ) : null}
      </div>

      <p className="font-sans text-xs text-ink-3">O código expira em cerca de 1 minuto.</p>

      <button
        type="button"
        onClick={() => void qr.refetch()}
        disabled={qr.isFetching || !qrEnabled}
        className={cn(
          'self-start font-sans text-sm font-medium px-3 py-2 rounded-sm',
          'border border-border-strong text-ink',
          'transition-colors duration-fast hover:bg-surface-hover',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-azul/20',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      >
        Gerar outro código
      </button>
    </div>
  );
}
